import { AfterViewInit, Component, ViewChild, OnInit, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { AlertController, IonRouterOutlet, ModalController, NavController, Platform } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "src/environments/environment";
import { AppService } from "./_services/app.service";
import { ConfigService } from "./_services/config.service";
import { PushNotificationService } from "./_services/firebase/push-notification.service";
import { GeoLocationService } from "./_services/geoLocation.service";
import { BackgroundLocationService } from "./_services/background-location.service";
import { LocalStorageService } from "./_services/localStorage.service";
import { GoogleMapsService } from "./_services/maps";
import { MessagesService } from "./_services/messages.service";
import { NavigationHistoryService } from "./_services/navigation-history.service";
import { NetworkService } from "./_services/network.service";
import { osService } from "./_services/osService.service";
import { NavigationService } from "./_services/navigation-service.service";
import { IosGestureService } from "./_services/ios-gesture.service";
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { KeyboardService } from "./_services/keyboard.service";
import {
  isFocusableFormField,
  measureKeyboardHeight,
  scheduleScrollFieldAboveKeyboard,
  scheduleScrollFocusedFieldAboveKeyboard,
} from "./helpers/keyboard-input-scroll";
import { PermissionStepperService } from "./_services/permission-stepper.service";
import { DiagnosticsConsentService } from "./_services/diagnostics-consent.service";
import { DiagnosticsConsentModalService } from "./_services/diagnostics-consent-modal.service";
import { LogClassifierService } from "./_services/log-classifier.service";
import { LogReporterService } from "./_services/log-reporter.service";
import { LogService } from "./_services/log.service";
import { RoleHelperService } from "./_services/role-helper.service";



@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
})
export class AppComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild(IonRouterOutlet) routerOutlet!: IonRouterOutlet;
  spalsh_Viewed: any = 0;
  isConnected: boolean = true;
  currentRoute = "/";
  routeStack: string[] = [];
  private keyboardSubscription: any;
  private keyboardListeners: any[] = [];
  private focusListeners: (() => void)[] = [];
  private appStateListenerHandles: any[] = [];
  private scrollCheckInterval: any;
  private overscrollObserver: MutationObserver | undefined;
  private consentPromptOpen = false;
  private consentPromptTimer: any;
  private consentRequestSub: any;
  private webHeartbeatInterval: any = null;
  private lastHeartbeatSentAt = 0;
  private readonly HEARTBEAT_MIN_INTERVAL_MS = 15_000;
  constructor(
    private navigationHistoryService: NavigationHistoryService,
    private translate: TranslateService,
    private navController: NavController,
    private configService: ConfigService,
    private geoLocationService: GeoLocationService,
    private backgroundLocationService: BackgroundLocationService,
    private osService: osService,
    private messagesService: MessagesService,
    private localStorageService: LocalStorageService, // getBuildVersion
    private appService: AppService,
    private route: Router,
    private router: Router, // Inject the Router service
    public platform: Platform,
    private googleMapsService: GoogleMapsService,
    private pushNotificationService: PushNotificationService,
    private networkService: NetworkService,
    private navigationService: NavigationService,
    private iosGestureService: IosGestureService,
    public plt: Platform,
    private keyboardService: KeyboardService, // private iab: InAppBrowser,
    private modalController: ModalController,
    private alertController: AlertController,
    private permissionStepper: PermissionStepperService,
    private roleHelper: RoleHelperService,
    private logService: LogService,
    private logReporter: LogReporterService,
    private logClassifier: LogClassifierService,
    private diagnosticsConsent: DiagnosticsConsentService,
    private diagnosticsConsentModal: DiagnosticsConsentModalService,
    private http: HttpClient // Inject HttpClient for heartbeat requests
  ) {
    // Boot in-app log capture as early as possible so we don't miss boot-time
    // errors. Idempotent — calling init() twice is a no-op.
    this.logService.init();
    // Reset the classifier's per-session upload counter for this app launch.
    this.logClassifier.resetSession();
    // Hydrate persisted consent. Auto-upload is controlled by the user's
    // Settings toggle. Non-consented users see the disclosure on first launch,
    // and support targeting can also request the same opt-in later.
    this.diagnosticsConsent.initOnBoot();
    // Show a non-blocking diagnostics opt-in prompt. Dismissal is remembered
    // 24h so we don't nag. The active-state indicator lives in Configurations
    // → "Aide au support technique" (persistent banner intentionally removed).
    // Subscription stored on `consentRequestSub` so ngOnDestroy can tear it
    // down — without this, hot-reload / re-bootstrap would stack alerts.
    this.consentRequestSub = this.diagnosticsConsent.consentRequest$.subscribe((req) => {
      if (req && req.matched && !this.consentPromptOpen) {
        if (this.consentPromptTimer) {
          clearTimeout(this.consentPromptTimer);
        }
        this.consentPromptTimer = setTimeout(() => {
          this.consentPromptTimer = null;
          if (!this.consentPromptOpen && !this.diagnosticsConsent.getConsent().granted) {
            void this.presentConsentRequestPrompt(req);
          }
        }, req.promptContext === "first_launch" ? 1200 : 0);
      }
    });
    // Wire auto-push of error bursts to the diagnostics endpoint. Gates on
    // user consent in production; unconditional in dev/staging.
    this.logReporter.init();
    // Initialize iOS gesture service to disable swipe-back globally
    this.iosGestureService.registerRouter(this.router);

    this.router.events.subscribe((event: any) => {
      if (event.url) {
        this.currentRoute = event.url;
        // Mettre à jour la pile de routes
        this.routeStack = this.navigationHistoryService.getRouteStack();
      }
    });
    translate.addLangs(["fr", "en", "it"]);
    const lang = localStorage.getItem("lang") ?? "fr";
    localStorage.setItem("lang", lang);
    translate.setDefaultLang(lang);
    translate.use(lang);
    const user = this.localStorageService.getToken();
    this.appService.getSplashViewed().subscribe((res: any) => {
      this.spalsh_Viewed = res;
    });
    this.plt.ready().then(async () => {
      geoLocationService.checkLocationPermission();
      this.assureLocationPermission();
      user && this.messagesService.initUnreadConversation();
      this.googleMapsService.initMapUrl();
      // Correction clavier mobile : que le clavier ne masque pas les inputs
      // Utiliser Ionic mode pour gérer automatiquement le scroll
      if (Capacitor.getPlatform() !== "web") {
        // Ionic mode gère automatiquement le scroll et le repositionnement
        Keyboard.setResizeMode({ mode: KeyboardResize.Ionic });
        Keyboard.setScroll({ isDisabled: false });
      } else if (this.plt.is('ios')) {
        // Pour iOS web, activer le scroll automatique
        Keyboard.setScroll({ isDisabled: false });
      }
      // Initialize background location tracking for providers
      await this.initializeBackgroundLocation();
    });

    this.setupPrestataireHeartbeat();
    this.initAndHideSplash();
    this.appService.refreshHomeDataStats();
  }

  private isPrestataireUser(): boolean {
    return this.roleHelper.isPrestataireUser(
      this.localStorageService.getUserData()?.client
    );
  }

  subscribeNetworkStatus() {
    this.networkService.getNetworkStatus().subscribe((connected) => {
      this.isConnected = connected;
      if (!connected) {
        this.navigationService.navigateTo("/network-error");
      } else {
        // Vérifier si on vient de la page d'erreur réseau
        const previousRoute = this.navigationHistoryService.getPreviousRoute();
        if (this.currentRoute === "/network-error") {
          if (previousRoute) {
            this.navigationService.goToPreviousPage();
          } else {
            this.navigationService.goHome();
          }
        }
      }
    });
  }
  ngOnInit() {
    // Verrouiller l'orientation en portrait uniquement sur les plateformes natives
    if (Capacitor.getPlatform() !== "web") {
      ScreenOrientation.lock({ orientation: 'portrait' }).catch((error) => {
      });
    }
    this.subscribeNetworkStatus();
    if ("File_native" in window) {
      window["File"] = window["File_native"] as any;
    }
    if ("FileReader_native" in window) {
      window["FileReader"] = window["FileReader_native"] as any;
    }
    if ("Blob_native" in window) {
      window["Blob"] = window["Blob_native"] as any;
    }
    if (this.spalsh_Viewed == 0 && !window.location.href?.includes("mobile.")) {
      this.navigationService.replace("/splash");
    }
    const user = this.localStorageService.getToken();
    user && this.pushNotificationService.initFcmPusher();

    if (user && this.isPrestataireUser()) {
      this.permissionStepper.maybeLaunchOnStart().catch(() => { });
    }

    // Setup global iOS keyboard scroll handling
    if (this.plt.is('ios')) {
      this.setupGlobalKeyboardScroll();
    }
  }

  ngOnDestroy() {
    this.cleanupGlobalKeyboardScroll();
    void this.backgroundLocationService.stopPresencePing();
    if (this.webHeartbeatInterval !== null) {
      clearInterval(this.webHeartbeatInterval);
      this.webHeartbeatInterval = null;
    }
    this.appStateListenerHandles.forEach((handle) => {
      try {
        handle?.remove?.();
      } catch (_) { }
    });
    this.appStateListenerHandles = [];
    if (this.consentRequestSub) {
      try { this.consentRequestSub.unsubscribe(); } catch (_) { }
      this.consentRequestSub = null;
    }
    if (this.consentPromptTimer) {
      clearTimeout(this.consentPromptTimer);
      this.consentPromptTimer = null;
    }
  }

  /**
   * Setup global keyboard scroll handling for all pages
   */
  private setupGlobalKeyboardScroll(): void {
    // Subscribe to keyboard state changes (same approach as messagerie)
    this.keyboardSubscription = this.keyboardService.getKeyboardState().subscribe(
      (isVisible: boolean) => {
        if (isVisible) {
          // When keyboard opens, ensure focused input is visible (same timing as messagerie)
          setTimeout(() => this.scrollToFocusedInput(), 100);
          setTimeout(() => this.scrollToFocusedInput(), 200);
          setTimeout(() => this.scrollToFocusedInput(), 300);
          setTimeout(() => this.scrollToFocusedInput(), 500);

          // Start continuous checking for iOS web (same as messagerie)
          if (!Capacitor.isNativePlatform()) {
            if (this.scrollCheckInterval) {
              clearInterval(this.scrollCheckInterval);
            }
            let checkCount = 0;
            this.scrollCheckInterval = window.setInterval(() => {
              this.scrollToFocusedInput();
              checkCount++;
              if (checkCount >= 20) { // Stop after 2 seconds (same as messagerie)
                clearInterval(this.scrollCheckInterval);
                this.scrollCheckInterval = undefined;
              }
            }, 100);
          }
        } else {
          // Stop continuous checking when keyboard closes
          if (this.scrollCheckInterval) {
            clearInterval(this.scrollCheckInterval);
            this.scrollCheckInterval = undefined;
          }
        }
      }
    );

    // Listen to native keyboard events
    if (Capacitor.isNativePlatform()) {
      const showListener = Keyboard.addListener('keyboardWillShow', (info) => {
        scheduleScrollFocusedFieldAboveKeyboard(info.keyboardHeight);
      });

      const didShowListener = Keyboard.addListener('keyboardDidShow', (info) => {
        scheduleScrollFocusedFieldAboveKeyboard(info.keyboardHeight, [100, 250]);
      });

      this.keyboardListeners.push(showListener, didShowListener);
    }

    // Use Visual Viewport API for iOS web
    if (window.visualViewport && !Capacitor.isNativePlatform()) {
      const viewportHandler = () => {
        const activeElement = document.activeElement as HTMLElement;
        if (isFocusableFormField(activeElement)) {
          setTimeout(() => this.scrollToFocusedInput(), 100);
          setTimeout(() => this.scrollToFocusedInput(), 300);
        }
      };

      window.visualViewport.addEventListener('resize', viewportHandler);
      window.visualViewport.addEventListener('scroll', viewportHandler);

      this.focusListeners.push(() => {
        window.visualViewport?.removeEventListener('resize', viewportHandler);
        window.visualViewport?.removeEventListener('scroll', viewportHandler);
      });
    }

    // Listen to all focus events globally with capture phase
    const globalFocusHandler = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (isFocusableFormField(target)) {
        // Immediate scroll attempt
        setTimeout(() => this.scrollToFocusedInput(), 50);
        setTimeout(() => this.scrollToFocusedInput(), 150);
        setTimeout(() => this.scrollToFocusedInput(), 300);
        setTimeout(() => this.scrollToFocusedInput(), 500);
        setTimeout(() => this.scrollToFocusedInput(), 700);
      }
    };

    document.addEventListener('focusin', globalFocusHandler, true);
    this.focusListeners.push(() => {
      document.removeEventListener('focusin', globalFocusHandler, true);
    });

    // Also listen to touch events on inputs (for iOS)
    const touchHandler = (event: TouchEvent) => {
      const target = event.target as HTMLElement;
      if (isFocusableFormField(target)) {
        setTimeout(() => {
          target.focus();
          setTimeout(() => this.scrollToFocusedInput(), 100);
          setTimeout(() => this.scrollToFocusedInput(), 300);
        }, 50);
      }
    };

    document.addEventListener('touchend', touchHandler, true);
    this.focusListeners.push(() => {
      document.removeEventListener('touchend', touchHandler, true);
    });
  }

  /**
   * Scroll to the currently focused input
   */
  private scrollToFocusedInput(): void {
    const keyboardHeight = Math.max(
      this.keyboardService.getCurrentKeyboardHeight(),
      measureKeyboardHeight()
    );
    scheduleScrollFocusedFieldAboveKeyboard(keyboardHeight);
  }

  private scrollToElement(element: HTMLElement): void {
    if (!isFocusableFormField(element)) {
      return;
    }

    const keyboardHeight = Math.max(
      this.keyboardService.getCurrentKeyboardHeight(),
      measureKeyboardHeight()
    );
    scheduleScrollFieldAboveKeyboard(element, keyboardHeight);
  }

  /**
   * Clean up global keyboard scroll handling
   */
  private cleanupGlobalKeyboardScroll(): void {
    if (this.keyboardSubscription) {
      this.keyboardSubscription.unsubscribe();
    }
    this.keyboardListeners.forEach(listener => {
      if (listener && listener.remove) {
        listener.remove();
      }
    });
    this.keyboardListeners = [];
    this.focusListeners.forEach(cleanup => cleanup());
    this.focusListeners = [];

    // Clean up scrollCheck interval (keyboard might be open when component destroys)
    if (this.scrollCheckInterval) {
      clearInterval(this.scrollCheckInterval);
      this.scrollCheckInterval = undefined;
    }

    // Clean up overscroll observer
    if (this.overscrollObserver) {
      this.overscrollObserver.disconnect();
      this.overscrollObserver = undefined;
    }
  }
  async ngAfterViewInit() {
    // Register router outlet and disable swipe-back globally
    if (this.routerOutlet) {
      this.iosGestureService.registerOutlet(this.routerOutlet);
      this.iosGestureService.disableSwipeBack();
    }

    // Disable overscroll/bounce on iOS
    if (this.plt.is('ios')) {
      this.disableIOSOverscroll();
    }

    setTimeout(async () => {
      await this.plt.ready();
      const user = this.localStorageService.getToken();
      if (user) {
        this.navigationService.pusherAuth();
        this.navigationService.subscribeToRoleChannel();
        this.navigationService.subscribeToDiscussionChannel();
      }
    }, 1000);

    if (Capacitor.getPlatform() !== "web") {
      App.addListener("appStateChange", ({ isActive }) => {
        if (isActive && this.localStorageService.isAuth() && this.isPrestataireUser()) {
          this.permissionStepper.checkAndPresentIfRevoked().catch(() => {});
          this.sendHeartbeat();
        }
      }).then((handle) => {
        this.appStateListenerHandles.push(handle);
      });
    }

  }

  /**
   * Non-blocking diagnostics disclosure shown on first launch, or later when
   * support targets this device during an investigation. The user can accept
   * (→ instant consent grant), say "Pas maintenant" (dismissed 24h), or read
   * more from Settings.
   */
  private async presentConsentRequestPrompt(status: any): Promise<void> {
    this.consentPromptOpen = true;
    const supportReason =
      status?.promptContext === "support_target" && status?.reason
        ? String(status.reason)
        : undefined;
    try {
      await this.appService.waitForLocationOnboardingToSettle();
      const accepted = await this.diagnosticsConsentModal.present({
        variant: "prompt",
        supportReason,
      });
      if (accepted) {
        this.diagnosticsConsent.grant();
      } else {
        this.diagnosticsConsent.dismissConsentRequest();
      }
      this.diagnosticsConsent.markFirstLaunchPromptSeen();
    } catch (_) {
      // Modal failed to open — do not block the app.
    } finally {
      this.consentPromptOpen = false;
    }
  }

  private disableIOSOverscroll(): void {
    // Prevent overscroll bounce on iOS using CSS
    document.body.style.overscrollBehavior = 'none';
    document.body.style.overscrollBehaviorY = 'none';
    document.documentElement.style.overscrollBehavior = 'none';
    document.documentElement.style.overscrollBehaviorY = 'none';

    // Apply to all ion-content elements and ensure they're scrollable
    const applyScrollStyles = () => {
      const ionContents = document.querySelectorAll('ion-content');
      ionContents.forEach((content: any) => {
        // Force scrollY attribute
        if (!content.hasAttribute('scrollY')) {
          content.setAttribute('scrollY', 'true');
        }

        if (content.shadowRoot) {
          const scrollElement = content.shadowRoot.querySelector('.inner-scroll') ||
            content.shadowRoot.querySelector('.scroll-content');
          if (scrollElement) {
            scrollElement.style.overscrollBehavior = 'none';
            scrollElement.style.overscrollBehaviorY = 'none';
            // Force scrollability
            scrollElement.style.overflowY = 'auto';
            scrollElement.style.webkitOverflowScrolling = 'touch';
            scrollElement.style.height = '100%';
            scrollElement.style.maxHeight = '100%';
          }
        }
      });
    };

    setTimeout(applyScrollStyles, 500);
    setTimeout(applyScrollStyles, 1000);
    setTimeout(applyScrollStyles, 2000);

    // Re-apply when ion-content elements are added/removed. Throttle via
    // requestAnimationFrame and filter mutations so dynamic Ionic pages/modals
    // are covered without re-scanning for unrelated DOM churn.
    let scheduled = false;
    const scheduleApply = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        applyScrollStyles();
      });
    };
    const mutationTouchesIonContent = (node: Node): boolean => {
      if (!(node instanceof Element)) {
        return false;
      }
      return node.matches("ion-content") || !!node.querySelector("ion-content");
    };
    this.overscrollObserver = new MutationObserver((mutations) => {
      const shouldApply = mutations.some((mutation) => {
        const nodes = [...Array.from(mutation.addedNodes), ...Array.from(mutation.removedNodes)];
        return nodes.some(mutationTouchesIonContent);
      });
      if (shouldApply) {
        scheduleApply();
      }
    });
    const observerRoot = document.querySelector("ion-app") ?? document.body;
    this.overscrollObserver.observe(observerRoot, {
      childList: true,
      subtree: true,
    });
  }

  private async getConfigs(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.configService.getConfigs().subscribe({
        next: (res: any) => {
          const data = res.body?.data;
          if (data) {
            this.localStorageService.setConfigs(data);
          }
          resolve();
        },
        error: () => resolve(), // Continue même en cas d'erreur
      });
    });
  }

  private async getSettings(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.configService.getSettings().subscribe({
        next: (res: any) => {
          const data = res.body?.data;
          if (data) {
            this.localStorageService.setSettings(data);
          }
          resolve();
        },
        error: () => resolve(), // Continue même en cas d'erreur
      });
    });
  }

  private async waitForStyles(): Promise<void> {
    // Attendre que les feuilles de style soient chargées
    return new Promise<void>((resolve) => {
      const maxWait = 4000; // 4 secondes maximum (réduit de 6s)
      const startTime = Date.now();

      const checkStyles = () => {
        const elapsed = Date.now() - startTime;

        // Après 1.5s, considérer les styles cross-origin comme chargés
        const crossOriginTimeout = elapsed > 1500;

        const allStylesheetsLoaded = Array.from(document.styleSheets).every((sheet) => {
          try {
            return sheet.cssRules && sheet.cssRules.length > 0;
          } catch (e) {
            // Cross-origin stylesheet, considérer comme chargé après le délai
            return crossOriginTimeout;
          }
        });

        // Vérifier aussi que les styles sont appliqués sur body
        const bodyStyles = window.getComputedStyle(document.body);
        const hasStyles = bodyStyles.fontFamily && bodyStyles.fontSize;

        // Si les styles de base sont appliqués OU si on a attendu assez longtemps
        if ((hasStyles && (allStylesheetsLoaded || crossOriginTimeout)) || elapsed > maxWait) {
          resolve();
        } else {
          setTimeout(checkStyles, 50); // Vérifier plus fréquemment (50ms au lieu de 100ms)
        }
      };

      // Commencer immédiatement
      checkStyles();
    });
  }

  private async waitForFonts(): Promise<void> {
    try {
      const fontsReady: Promise<void> | undefined = (document as any).fonts?.ready;
      if (fontsReady) {
        await Promise.race([
          fontsReady as Promise<void>,
          new Promise<void>((resolve) => setTimeout(resolve, 3000)), // Réduit de 5s à 3s
        ]);
      } else {
        // Fallback: attendre un délai raisonnable pour les polices externes
        await new Promise<void>((resolve) => setTimeout(resolve, 1500)); // Réduit de 2s à 1.5s
      }
    } catch {
      // Fallback si fonts API n'est pas disponible
      await new Promise<void>((resolve) => setTimeout(resolve, 1500)); // Réduit de 2s à 1.5s
    }
  }

  private async initAndHideSplash() {
    await this.plt.ready();

    // Attendre les styles CSS en PRIORITÉ (doit être rapide)
    const stylesPromise = this.waitForStyles();

    // Attendre les polices en parallèle (mais ne pas bloquer si prend trop de temps)
    const fontsPromise = this.waitForFonts();

    // Attendre les données critiques avec timeout plus court (4s au lieu de 8s)
    const criticalDataPromise = Promise.race([
      Promise.all([
        this.getConfigs(),
        this.getSettings(),
      ]),
      new Promise<void>((resolve) => setTimeout(resolve, 4000)), // Réduit de 8s à 4s
    ]);

    // Attendre le minimum requis : styles + délai minimum
    await Promise.race([
      Promise.all([
        stylesPromise,
        fontsPromise,
        criticalDataPromise,
      ]),
      // Timeout global maximum : 5 secondes (pour éviter blocage)
      new Promise<void>((resolve) => setTimeout(resolve, 5000)),
    ]);

    // Vérifier si on va afficher le splash Angular - si oui, attendre un peu plus
    const willShowSplash = this.spalsh_Viewed == 0 && !window.location.href?.includes("mobile.");

    if (willShowSplash) {
      // Attendre un peu plus pour que le composant splash Angular soit prêt
      await new Promise<void>((resolve) => setTimeout(resolve, 500));
    } else {
      // Petit délai supplémentaire pour s'assurer que tout est appliqué
      await new Promise<void>((resolve) => setTimeout(resolve, 200));
    }

    await SplashScreen.hide();
    // Masquer un éventuel splash personnalisé HTML si présent
    const splash = document.getElementById("custom-splash");
    if (splash) {
      splash.style.display = "none";
    }
  }

  /**
   * Initialize background location tracking for providers
   * IMPORTANT: Only start tracking if provider session is active (current_state === true)
   * Tracking should be controlled by startDay()/endDay() in provider component
   */
  async initializeBackgroundLocation() {
    try {
      const user = this.localStorageService.getUserData();
      const role = user?.client?.roles?.[0];
      const currentState = user?.client?.current_state;

      // Only start background tracking for providers (prestataire) AND if session is active
      // At startup: only start if we already have "always" (do not ask or show modal at startup)
      if (role === 'prestataire' && currentState === true) {
        const isTracking = this.backgroundLocationService.isTrackingActive();
        if (!isTracking) {
          const authStatus = await this.backgroundLocationService.getLocationAuthorizationStatus();
          if (authStatus === 'always') {
            await this.backgroundLocationService.startTracking();

          }
        }
      } else {
        // Provider session is not active - ensure tracking is stopped
        const isTracking = this.backgroundLocationService.isTrackingActive();
        if (isTracking) {
          await this.backgroundLocationService.stopTracking();
        }
      }
    } catch (error) {
      console.error('[AppComponent] Error initializing background location:', error);
    }
  }

  async assureLocationPermission() {
    if (!this.localStorageService.isAuth()) {
      return;
    }

    const hasPermission =
      await this.geoLocationService.checkLocationPermission();
    if (hasPermission) {
      this.osService.getCurrentLocation();
      return;
    }

    const choice = await this.appService.presentProviderLocationDisclosureIfNeeded();
    if (choice === "accepted") {
      const permissionGranted =
        await this.geoLocationService.requestLocationPermission();
      if (permissionGranted) {
        this.osService.getCurrentLocation();
      }
    }
  }

  // Méthodes pour gérer la pile de routes
  getRouteStack(): string[] {
    return this.navigationHistoryService.getRouteStack();
  }

  getPreviousRoute(): string | null {
    return this.navigationHistoryService.getPreviousRoute();
  }

  getCurrentRouteFromStack(): string | null {
    return this.navigationHistoryService.getCurrentRoute();
  }

  getRouteStackLength(): number {
    return this.navigationHistoryService.getRouteStackLength();
  }

  hasRouteInHistory(route: string): boolean {
    return this.navigationHistoryService.hasRouteInHistory(route);
  }

  getRouteIndex(route: string): number {
    return this.navigationHistoryService.getRouteIndex(route);
  }

  clearRouteHistory() {
    this.navigationHistoryService.clearHistory();
    this.routeStack = [];
  }

  // Méthode pour naviguer vers une route précédente spécifique
  navigateToPreviousRoute(targetRoute?: string) {
    if (targetRoute) {
      const routeIndex = this.getRouteIndex(targetRoute);
      if (routeIndex !== -1) {
        // Naviguer vers la route spécifiée
        this.router.navigate([targetRoute]);
      }
    } else {
      // Naviguer vers la route précédente
      const previousRoute = this.getPreviousRoute();
      if (previousRoute) {
        this.router.navigate([previousRoute]);
      }
    }
  }

  initializeApp() { }

  /**
   * Native presence ping for prestataire (15s, works in background via FG service).
   * Web keeps a lightweight JS fallback timer. Immediate ping on setup and on
   * foreground resume (see appStateChange in ngAfterViewInit) closes the login
   * race window and iOS background throttling gaps.
   */
  private setupPrestataireHeartbeat(): void {
    const currentUserData = this.localStorageService.getUserData();
    const isAuthenticated = !!currentUserData?.token;
    const isPrestataire = this.roleHelper.isPrestataireUser(currentUserData?.client);

    if (!isAuthenticated || !isPrestataire) {
      return;
    }

    if (Capacitor.getPlatform() === "web") {
      this.startWebHeartbeatFallback();
      return;
    }

    void this.backgroundLocationService.startPresencePing();
    this.sendHeartbeat();
  }

  private startWebHeartbeatFallback(): void {
    if (this.webHeartbeatInterval !== null) {
      clearInterval(this.webHeartbeatInterval);
    }
    this.webHeartbeatInterval = window.setInterval(() => {
      if (
        this.localStorageService.getToken() &&
        this.isPrestataireUser()
      ) {
        this.sendHeartbeat();
      }
    }, 15_000);
  }

  /**
   * Immediate heartbeat (foreground catch-up or web).
   * Throttled: no more than once per HEARTBEAT_MIN_INTERVAL_MS to prevent
   * rapid appStateChange events from hammering the endpoint.
   */
  private sendHeartbeat(): void {
    if (!this.localStorageService.getToken() || !this.isPrestataireUser()) {
      return;
    }
    const now = Date.now();
    if (now - this.lastHeartbeatSentAt < this.HEARTBEAT_MIN_INTERVAL_MS) {
      return;
    }
    this.lastHeartbeatSentAt = now;
    this.http
      .post(`${environment.apiUrl}/auth/frontoffice/me/heartbeat`, {}, {
        observe: "response",
      })
      .subscribe({
        next: () => {
          console.debug("[AppComponent] Heartbeat sent (JS)");
        },
        error: (error) => {
          console.warn("[AppComponent] Heartbeat failed (JS):", error);
        },
      });
  }
}
